import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ExpandableMenuItemContent, ExpandableMenuItemTrigger } from '@atlaskit/navigation-system';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { SECTION_ITEM_PROJECTS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PROJECTS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { Nav4ProjectsContent } from '../../../common/ui/content/Nav4ProjectsContent.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { Footer } from '../../../common/ui/footer/index.tsx';
import type {
	Nav4ProjectsForLanding$key,
	Nav4ProjectsForLanding$data,
} from './__generated__/Nav4ProjectsForLanding.graphql';

type Nav4ProjectsForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4ProjectsForLanding$key;
	canCreateProject: boolean;
};

export function Nav4ProjectsForLanding(props: Nav4ProjectsForLandingProps): React.JSX.Element {
	const { queryRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const data = useFragment<Nav4ProjectsForLanding$key>(
		graphql`
			fragment Nav4ProjectsForLanding on Query {
				...Nav4ProjectsContent
			}
		`,
		queryRef,
	);

	const menuId = MENU_ID_PROJECTS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PROJECTS,
					sectionItem: SECTION_ITEM_PROJECTS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ProjectsForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={menuId}
			queryRef={data}
		/>
	);
}

const Nav4ProjectsForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4ProjectsForLandingInnerGSN,
	Nav4ProjectsForLandingInnerNav4,
);

type InnerProps = Omit<Nav4ProjectsForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4ProjectsForLanding$data;
};

function Nav4ProjectsForLandingInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		canCreateProject,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-projects-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-projects-landing">
					{fg('blu-8569_add_holdout_data_to_nav_ufo_segments') && <JiraHoldoutCustomData />}
					<JSErrorBoundary
						id="nav4-sidebar-projects-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						{/* This component uses the fragment from the ancestor query. */}
						<Nav4ProjectsContent queryRef={queryRef} />
					</JSErrorBoundary>
				</UFOSegment>
				{!expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) && (
					<Footer canCreateProject={canCreateProject} />
				)}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4ProjectsForLandingInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		canCreateProject,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon + text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				testId={getTestId(menuId)}
				interactionName="nav4-sidebar-projects-landing"
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-projects-landing">
					{fg('blu-8569_add_holdout_data_to_nav_ufo_segments') && <JiraHoldoutCustomData />}
					<JSErrorBoundary
						id="nav4-sidebar-projects-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						{/* This component uses the fragment from the ancestor query. */}
						<Nav4ProjectsContent queryRef={queryRef} />
					</JSErrorBoundary>
				</UFOSegment>
				{!expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) && (
					<Footer canCreateProject={canCreateProject} />
				)}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
